import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";

const TYPES = [
  { value: "BIMENSUELLE", label: "Inspection bimensuelle des fronts" },
  { value: "QUOTIDIENNE_AVANT_POSTE", label: "Visite avant prise de poste" },
  { value: "APRES_TIR", label: "Visite après tir de mine" },
];

export default function AdminQuestions() {
  const [type, setType] = useState("BIMENSUELLE");
  const [questions, setQuestions] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ section: "", texte: "", est_critique: false, ordre: 0 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/questions?type_inspection=${type}&include_inactive=${showInactive}`).then(({ data }) => setQuestions(data));
  useEffect(() => { load(); }, [type, showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/questions", { ...form, type_inspection: type });
      setForm({ section: "", texte: "", est_critique: false, ordre: 0 });
      setShowForm(false);
      load();
    } finally { setBusy(false); }
  };

  const toggleActif = async (q) => {
    const wasActive = String(q.Actif).toUpperCase() === "TRUE";
    await api.put(`/admin/questions/${q.Question_ID}`, { actif: !wasActive });
    if (wasActive) {
      toast.info("Question désactivée (pas supprimée)", {
        description: "Elle n'apparaîtra plus dans les nouvelles inspections. Cochez « Afficher les questions désactivées » pour la réactiver à tout moment.",
      });
    } else {
      toast.success("Question réactivée", { description: "Elle apparaîtra à nouveau dans les inspections." });
    }
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <select data-testid="admin-questions-type-select" value={type} onChange={(e) => setType(e.target.value)} className="bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100">
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <Button data-testid="admin-questions-add-button" onClick={() => setShowForm(!showForm)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter une question
        </Button>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400">
        <Checkbox data-testid="admin-questions-show-inactive-checkbox" checked={showInactive} onCheckedChange={(v) => setShowInactive(!!v)} />
        Afficher les questions désactivées
      </label>
      {showForm && (
        <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 grid sm:grid-cols-2 gap-3">
          <Input data-testid="admin-question-section-input" placeholder="Section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100 sm:col-span-2" />
          <Input data-testid="admin-question-texte-input" placeholder="Texte de la question" value={form.texte} onChange={(e) => setForm({ ...form, texte: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100 sm:col-span-2" />
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Checkbox data-testid="admin-question-critique-checkbox" checked={form.est_critique} onCheckedChange={(v) => setForm({ ...form, est_critique: v })} /> Critique (déclenche suspension)
          </label>
          <Button data-testid="admin-question-submit-button" disabled={busy || !form.texte} onClick={create} className="sm:col-span-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {questions.map((q) => {
          const isActive = String(q.Actif).toUpperCase() === "TRUE";
          return (
            <div key={q.Question_ID} data-testid={`admin-question-row-${q.Question_ID}`} className={`rounded-lg border border-slate-800 bg-[#121820] p-3 flex items-center justify-between flex-wrap gap-2 ${isActive ? "" : "opacity-50"}`}>
              <div>
                <div className="text-sm font-semibold text-slate-100">
                  {q.Question} {q.Criticite_Anomalie === "CRITIQUE" && <span className="text-red-400 text-xs ml-1">[CRITIQUE]</span>}
                  {!isActive && <span data-testid={`admin-question-inactive-badge-${q.Question_ID}`} className="text-slate-500 text-xs ml-1 border border-slate-700 rounded px-1.5 py-0.5">Désactivée</span>}
                </div>
                <div className="text-xs text-slate-500">{q.Section}</div>
              </div>
              <Button data-testid={`admin-question-toggle-${q.Question_ID}`} size="sm" variant="outline" onClick={() => toggleActif(q)} className="border-slate-700 text-xs">
                {isActive ? "Désactiver" : "Activer"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
